import { useLayoutEffect, useRef, useState } from "react";

const IMG_W = 509;   // intrinsic vial.png dimensions
const IMG_H = 1226;
const IMG_ASPECT = IMG_W / IMG_H;

// Name band as a fraction of the rendered vial image (the blank label area)
const BAND = { left: 0.24, width: 0.52, top: 0.62, height: 0.095 };

export function VialImage({ name, showName = true, large = false, testid }) {
  const boxRef = useRef(null);
  const wrapRef = useRef(null);
  const textRef = useRef(null);
  const [geo, setGeo] = useState(null);

  useLayoutEffect(() => {
    if (!showName || !name) return;
    const box = boxRef.current;
    if (!box) return;

    const place = () => {
      const bw = box.clientWidth;
      const bh = box.clientHeight;
      if (!bw || !bh) return;
      // object-contain rendered image size + centering offset
      const scale = Math.min(bw / IMG_W, bh / IMG_H);
      const rw = IMG_W * scale;
      const rh = IMG_H * scale;
      const ox = (bw - rw) / 2;
      const oy = (bh - rh) / 2;
      setGeo({
        left: ox + BAND.left * rw,
        top: oy + BAND.top * rh,
        width: BAND.width * rw,
        height: BAND.height * rh,
      });
    };

    place();
    const ro = new ResizeObserver(place);
    ro.observe(box);
    return () => ro.disconnect();
  }, [name, showName, large]);

  useLayoutEffect(() => {
    if (!geo) return;
    const wrap = wrapRef.current;
    const text = textRef.current;
    if (!wrap || !text) return;
    const availW = wrap.clientWidth;
    const availH = wrap.clientHeight;
    let size = Math.floor(Math.min(availH * 1.25, availW * 0.5));
    if (size < 4) size = 4;
    text.style.fontSize = size + "px";
    let guard = 60;
    while (guard-- > 0 && size > 4 && (text.scrollWidth > availW || text.scrollHeight > availH)) {
      size -= 1;
      text.style.fontSize = size + "px";
    }
  }, [geo, name]);

  return (
    <div ref={boxRef} className="relative w-full h-full flex items-center justify-center bg-white" data-testid={testid}>
      <img src="/vial.png" alt={name || "Mulan Peptide vial"} className="h-full w-full object-contain" />
      {showName && name && geo && (
        <div
          ref={wrapRef}
          className="absolute flex items-center justify-center overflow-hidden"
          style={{ left: geo.left, top: geo.top, width: geo.width, height: geo.height }}
        >
          <span
            ref={textRef}
            className="w-full text-center font-semibold uppercase tracking-tight leading-[1.02] text-[#8A6D1B]"
            style={{ fontFamily: "'Cinzel', serif", wordBreak: "normal", overflowWrap: "normal", hyphens: "none" }}
          >
            {name}
          </span>
        </div>
      )}
    </div>
  );
}
